import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactNode,
} from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { cn } from '../../../utils';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'agn-dialog-overlay fixed inset-0 z-50 bg-slate-950/55 backdrop-blur-sm',
      'motion-safe:data-[state=open]:animate-in motion-safe:data-[state=closed]:animate-out',
      'motion-safe:data-[state=closed]:fade-out-0 motion-safe:data-[state=open]:fade-in-0',
      'motion-safe:duration-150',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const dialogContentVariants = cva(
  cn(
    'agn-dialog-content fixed z-50',
    'w-full',
    'flex flex-col',
    'bg-white dark:bg-slate-900',
    'border border-slate-200 dark:border-slate-800',
    'rounded-xl shadow-xl shadow-slate-950/10 dark:shadow-slate-950/40',
    'focus:outline-none',
    'motion-safe:data-[state=open]:animate-in motion-safe:data-[state=closed]:animate-out',
    'motion-safe:data-[state=closed]:fade-out-0 motion-safe:data-[state=open]:fade-in-0',
    'motion-safe:data-[state=closed]:zoom-out-[0.98] motion-safe:data-[state=open]:zoom-in-[0.98]',
    'motion-safe:duration-150',
    'motion-safe:ease-[cubic-bezier(0.16,1,0.3,1)]'
  ),
  {
    variants: {
      // Where the panel sits. 'center' is the classic modal. 'docked'
      // carries no position at all: the consumer places the panel with
      // its own className or style (a side sheet beside a canvas).
      //
      // The centring lives in this variant, not the base, because it
      // cannot be undone reliably from outside. Tailwind v4 compiles
      // translate-x-[-50%] / translate-y-[-50%] to the CSS `translate`
      // property, which an inline `transform: none` does not cancel; a
      // consumer that repositioned a centred panel that way got it
      // shifted half its own size up and left, off-screen (Agion#2177).
      placement: {
        center: 'left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]',
        docked: '',
      },
      size: {
        sm: 'max-w-sm',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
        full: 'max-w-[min(96vw,72rem)] h-[min(92vh,56rem)]',
        // 'screen' is the IDE-style layout for surfaces where the user
        // is editing code — Monaco needs real horizontal real estate
        // and the bottom 30% of 'full' is wasted space. Width nearly
        // fills the viewport; height nearly does too. Used by the
        // tool editor (ToolDialog).
        screen: 'max-w-[min(98vw,120rem)] h-[min(96vh,68rem)]',
      },
      padding: {
        default: 'p-6 gap-4',
        none: 'p-0 gap-0',
      },
    },
    defaultVariants: {
      placement: 'center',
      size: 'md',
      padding: 'default',
    },
  }
);

export type DialogContentVariants = VariantProps<typeof dialogContentVariants>;

export interface DialogContentProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    DialogContentVariants {
  /**
   * Hide the built-in close (X) button. Use when the consumer renders its
   * own close affordance in a custom header.
   */
  hideCloseButton?: boolean;
  /**
   * Cap the content height to a fraction of viewport. Adds `overflow-hidden`
   * so a child <DialogBody> can scroll without breaking the rounded corners.
   * `size="full"` already constrains height; this is for sm/md/lg/xl.
   */
  maxHeight?: 'auto' | '90vh';
}

export const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      placement,
      size,
      padding,
      hideCloseButton = false,
      maxHeight = 'auto',
      ...props
    },
    ref
  ) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          dialogContentVariants({ placement, size, padding }),
          maxHeight === '90vh' && 'max-h-[90vh] overflow-hidden',
          className
        )}
        {...props}
      >
        {children}
        {!hideCloseButton && (
          <DialogPrimitive.Close
            className={cn(
              'agn-dialog-close absolute right-3 top-3 z-10',
              'inline-flex h-8 w-8 items-center justify-center rounded-md',
              'text-slate-500 dark:text-slate-400',
              'transition-colors',
              'hover:text-slate-900 hover:bg-slate-100',
              'dark:hover:text-slate-100 dark:hover:bg-slate-800',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400',
              'dark:focus-visible:ring-slate-600',
              'disabled:pointer-events-none disabled:opacity-40'
            )}
            aria-label="Close dialog"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const dialogIconVariants = cva(
  cn(
    'inline-flex shrink-0 items-center justify-center',
    'h-9 w-9 rounded-lg',
    '[&>svg]:h-[18px] [&>svg]:w-[18px]'
  ),
  {
    variants: {
      tone: {
        neutral:
          'bg-slate-100 text-slate-600 dark:bg-slate-500/10 dark:text-slate-300',
        info: 'bg-sky-500/10 text-sky-600 dark:bg-sky-500/10 dark:text-sky-300',
        success:
          'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300',
        warning:
          'bg-amber-500/10 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300',
        destructive:
          'bg-rose-500/10 text-rose-600 dark:bg-rose-500/10 dark:text-rose-300',
        brand:
          'bg-amber-500/10 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300',
      },
    },
    defaultVariants: {
      tone: 'neutral',
    },
  }
);

export type DialogIconVariants = VariantProps<typeof dialogIconVariants>;

export interface DialogIconProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    DialogIconVariants {
  children: ReactNode;
}

export const DialogIcon = forwardRef<HTMLSpanElement, DialogIconProps>(
  ({ className, tone, children, ...props }, ref) => (
    <span
      ref={ref}
      aria-hidden
      className={cn('agn-dialog-icon', dialogIconVariants({ tone }), className)}
      {...props}
    >
      {children}
    </span>
  )
);
DialogIcon.displayName = 'DialogIcon';

export const DialogEyebrow = forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'inline-block text-[11px] font-medium uppercase tracking-[0.08em]',
      'text-slate-500 dark:text-slate-400',
      className
    )}
    {...props}
  />
));
DialogEyebrow.displayName = 'DialogEyebrow';

export interface DialogHeaderProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Optional icon rendered to the left of the title block. */
  icon?: ReactNode;
}

export const DialogHeader = forwardRef<HTMLDivElement, DialogHeaderProps>(
  ({ className, icon, children, ...props }, ref) => {
    if (icon) {
      return (
        <div
          ref={ref}
          className={cn('agn-dialog-header flex items-start gap-3 text-left', className)}
          {...props}
        >
          {icon}
          <div className="flex flex-1 flex-col space-y-1.5 min-w-0">
            {children}
          </div>
        </div>
      );
    }
    return (
      <div
        ref={ref}
        className={cn('agn-dialog-header flex flex-col space-y-1.5 text-left', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);
DialogHeader.displayName = 'DialogHeader';

const dialogBodyVariants = cva(
  cn(
    'min-h-0 flex-1',
    // CSS-only top/bottom fade so users get a scroll affordance without JS.
    // The mask is invisible when content fits and gracefully fades long content.
    '[mask-image:linear-gradient(to_bottom,transparent_0,black_12px,black_calc(100%-12px),transparent_100%)]'
  ),
  {
    variants: {
      scroll: {
        auto: 'overflow-y-auto',
        none: 'overflow-visible',
      },
      padding: {
        default: '',
        comfortable: 'space-y-4',
        compact: 'space-y-2',
        none: '',
      },
      bleed: {
        // Used inside DialogContent padding="none" — body adds its own inset.
        true: 'px-6 py-5',
        false: '',
      },
    },
    defaultVariants: {
      scroll: 'auto',
      padding: 'comfortable',
      bleed: false,
    },
  }
);

export type DialogBodyVariants = VariantProps<typeof dialogBodyVariants>;

export interface DialogBodyProps
  extends React.HTMLAttributes<HTMLDivElement>,
    DialogBodyVariants {}

export const DialogBody = forwardRef<HTMLDivElement, DialogBodyProps>(
  ({ className, scroll, padding, bleed, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'agn-dialog-body',
        dialogBodyVariants({ scroll, padding, bleed }),
        className
      )}
      {...props}
    />
  )
);
DialogBody.displayName = 'DialogBody';

const dialogFooterVariants = cva(
  cn(
    'flex flex-col-reverse gap-2',
    'sm:flex-row sm:justify-end sm:gap-2'
  ),
  {
    variants: {
      tone: {
        // Plain footer — inherits parent padding/background. Existing default.
        plain: '',
        // Subtle stage for primary CTA — top border + faint surface tint.
        // Use with DialogContent padding="none" so the footer can bleed
        // edge-to-edge.
        framed: cn(
          'border-t border-slate-200 dark:border-slate-800/60',
          'bg-slate-50/60 dark:bg-slate-900/40',
          'px-6 py-4'
        ),
      },
    },
    defaultVariants: {
      tone: 'plain',
    },
  }
);

export type DialogFooterVariants = VariantProps<typeof dialogFooterVariants>;

export interface DialogFooterProps
  extends React.HTMLAttributes<HTMLDivElement>,
    DialogFooterVariants {}

export const DialogFooter = forwardRef<HTMLDivElement, DialogFooterProps>(
  ({ className, tone, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('agn-dialog-footer', dialogFooterVariants({ tone }), className)}
      {...props}
    />
  )
);
DialogFooter.displayName = 'DialogFooter';

export const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'agn-dialog-title text-base font-semibold leading-snug tracking-tight',
      'text-slate-900 dark:text-slate-100',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

export const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(
      'text-sm leading-relaxed text-slate-500 dark:text-slate-400',
      className
    )}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

/**
 * useDialogScrolled — observes a scroll container and returns whether
 * the user has scrolled past the top. Lets a sticky header reveal a
 * hairline divider only when content has moved underneath it.
 *
 * Usage:
 *   const bodyRef = useRef<HTMLDivElement>(null)
 *   const scrolled = useDialogScrolled(bodyRef)
 *   <header data-scrolled={scrolled}> ... </header>
 *   <DialogBody ref={bodyRef}> ... </DialogBody>
 */
export function useDialogScrolled<T extends HTMLElement>(
  ref: React.RefObject<T | null>
): boolean {
  const [scrolled, setScrolled] = useState(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => {
      rafRef.current = null;
      setScrolled(el.scrollTop > 1);
    };
    const onScroll = () => {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(update);
    };

    update();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [ref]);

  return scrolled;
}
